








































































































































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import CountriesStore from "@/mixins/constants/countriesStore";
import AdminBrandId from "@/mixins/adminBrandId";
import AdminBrands from "@/mixins/adminBrands";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import {
  FraudEntities,
  FraudLevels,
  FraudTypes,
  FraudDetectionTypes
} from "@/data/fraud";

// Types
import { IState } from "@/store";
import { IFraudSetting } from "@/models/fraud";
import { ICountry } from "@/models/constants";
import { UUID } from "@/data/enums";

@Component({
  computed: {
    ...mapState({
      brandCountryId: (state: IState) => state.brand.country_id
    })
  }
})
export default class AddEditFraudDetectionRuleModal extends Mixins(
  RequestPromises,
  HasValidation,
  CountriesStore,
  AdminBrandId,
  AdminBrands
) {
  //Props
  @Prop({ default: null })
  public rule!: IFraudSetting;

  @Prop({ default: FraudDetectionTypes.COUNTRY })
  public type!: IFraudSetting["type"];

  // Data
  public brandCountryId!: ICountry["id"];
  public isLoading = false;
  public isProcessing = false;

  public entities = FraudEntities();
  public types = FraudTypes();
  public levels = FraudLevels();

  public form: IFraudSetting = {
    brand_id: this.brandId,
    entity: this.entities[0].id,
    type: this.types[0].id,
    level: this.levels[0].id,
    value: null, // IP address or Country code,
    comment: ""
  };

  // Lifecycle methods
  public async created() {
    this.form.brand_id = this.brandId;

    if (!this.isNewRule) {
      this.form = this.$_.merge(this.form, this.rule);
    } else {
      this.onTypeChange(this.$_.find(this.types, ["id", this.type]));
    }
  }

  // Computed
  public get isIP() {
    return this.form.type === FraudDetectionTypes.IP;
  }
  public get isCountry() {
    return this.form.type === FraudDetectionTypes.COUNTRY;
  }

  public get hasMoreThanOneBrand() {
    return this.brands.length > 1;
  }

  // Methods
  public onTypeChange(type) {
    this.form.value = null;
    this.form.type = type.id;

    if (this.isCountry) {
      // @ts-ignore
      this.form.value = this.$_.get(
        this.countries.find(country => country.id === this.brandCountryId),
        "code"
      );
    }
  }

  public async validateBeforeSubmit() {
    if (!this.canSave) {
      return;
    }

    this.isProcessing = true;

    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      const result = await this.saveRule();

      this.$buefy.toast.open({
        message: this.$t("_sentence.successfully_saved") as string
      });

      this.$emit("success", result);
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public saveRule() {
    return this.$store.dispatch(
      `data/${DataModules.FRAUD_DETECTION}/${
        this.isNewRule ? "create" : "update"
      }`,
      this.$_.merge(
        { data: this.form },
        this.isNewRule ? {} : { id: this.rule.id }
      )
    );
  }

  public get isNewRule() {
    return this.$_.isEmpty(this.rule);
  }

  public get modalTitle() {
    return this.$t(
      this.isNewRule ? "_action.add_object" : "_action.edit_object",
      {
        object: this.isCountry
          ? this.$tc("_.country_fraud_rules", 1).toLowerCase()
          : this.$tc("_.ip_fraud_rules", 1).toLowerCase()
      }
    );
  }

  public canSave() {
    if (this.isNewRule) {
      return this.form.entity === "brand"
        ? this.$userCan("create_brand_fraud_check_settings")
        : this.$userCan("create_organisation_fraud_check_settings");
    }

    return this.form.entity === "brand"
      ? this.$userCan("update_brand_fraud_check_settings")
      : this.$userCan("update_organisation_fraud_check_settings");
  }

  public get contentAvailable() {
    if (this.isNewRule)
      return (
        this.$userCan("create_brand_fraud_check_settings") ||
        this.$userCan("create_organisation_fraud_check_settings")
      );

    return (
      this.$userCan("update_brand_fraud_check_settings") ||
      this.$userCan("update_organisation_fraud_check_settings")
    );
  }

  @Watch("form.brand_id")
  public onBrandIdChange() {
    this.$set(
      this.form,
      "entity",
      this.form.brand_id !== UUID.ORG ? "brand" : "organisation"
    );
  }
}
